import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sendResetPasswordEmail } from "../services/firebaseAuthService"; // Import the password reset function
import image from "../asset/image.jpeg";
import { useAuth } from "../context/AuthContext"; // Import useAuth to check authentication status
import TextField from '@mui/material/TextField';
import { motion, AnimatePresence } from "framer-motion";

function ForgotPasswordPage() {
  const { currentUser,isAnonymous } = useAuth();  // Access the current user from the AuthContext
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode !== null ? savedMode === 'true' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [hoveredButton, setHoveredButton] = useState(null); // Track which button is hovered
  const navigate = useNavigate();
  
  useEffect(() => {
    if (currentUser && !isAnonymous) {
      navigate("/chat");
    } else {
      setIsLoading(false); // Stop showing loading screen
    }
  }, [currentUser, isAnonymous, navigate]);

  useEffect(() => {
  if (popupMessage) {
    const timer = setTimeout(() => {
      setPopupMessage("");
    }, 2000); // 2000ms = 4 seconds

    return () => clearTimeout(timer); // Clear timer on unmount or when popupMessage changes
  }
}, [popupMessage]);

  

  useEffect(() => {
    localStorage.setItem('darkMode', isDarkMode);

    if (isDarkMode) {
      document.body.classList.add('dark-mode');
      document.documentElement.classList.add('dark');
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const darkModeTheme = {
    background: '#17191E',
    text: 'white',
    border: 'border-gray-700',
    hoverBackground: 'hover:bg-gray-800',
    iconColor: 'text-yellow-400',
    buttonColor: 'bg-gray-700',
    buttonHover: 'hover:bg-gray-600'
  };
  
  const lightModeTheme = {
    background: 'white',
    text: 'text-gray-900',
    border: 'border-gray-200',
    hoverBackground: 'hover:bg-gray-100',
    iconColor: 'text-gray-700',
    buttonColor: 'bg-gray-200',
    buttonHover: 'hover:bg-gray-300'
  };
  

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await sendResetPasswordEmail(email);
      if (response.success) {
        setPopupMessage("Password reset email sent successfully!");
        setPopupType("success");
      } else {
        setPopupMessage(response.message);
        setPopupType("error");
      }
    } catch (error) {
      setPopupMessage("An error occurred. Please try again.");
      setPopupType("error");
    }
  };

  return (
    <div
      style={{ backgroundColor: 'white' }}
      className="min-h-screen flex flex-col items-center justify-center p-6 md:p-10 relative"
    >
      <div className="absolute top-6 left-8 flex items-center">
        <img 
          src="/logo.png" 
          alt="BlippAI" 
          className="h-8 w-auto mr-2" 
        />
        <span className="text-2xl font-bold text-black" style={{fontFamily: 'OpenAI Sans, SF Pro, -apple-system, BlinkMacSystemFont, Helvetica, sans-serif', fontWeight: 500, letterSpacing: '-.02em'}}>BlippAI</span>
      </div>
          {/* AnimatePresence wrapper around the popup */}
          <AnimatePresence>
            {popupMessage && (
              <motion.div
                key="popup"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className={`fixed top-6 right-8 z-50 w-80 p-4 rounded-lg text-white shadow-lg
                  ${
                    popupType === "success"
                      ? "bg-green-600"
                      : popupType === "info"
                      ? "bg-blue-600"
                      : "bg-red-600"
                  }`}
              >
                {popupMessage}
              </motion.div>
            )}
          </AnimatePresence>
      <div className="w-full max-w-md mx-auto flex flex-col items-center justify-center">
        <h1 className="text-4xl font-bold text-black mb-10 text-center" style={{fontFamily: 'OpenAI Sans, SF Pro, -apple-system, BlinkMacSystemFont, Helvetica, sans-serif', fontWeight: 500, letterSpacing: '-.02em'}}>Reset your password</h1>
        <form onSubmit={handleResetPassword} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <TextField
            label="Email address"
            variant="outlined"
            value={email}
            onChange={e => setEmail(e.target.value)}
            fullWidth
            required
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#cdcdcd',
                },
                '&:hover fieldset': {
                  borderColor: '#10a37f',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#10a37f',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#b0b0b0',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#10a37f',
              },
            }}
            style={{ marginBottom: '.5rem' }}
          />
          <button
            type="submit"
            onMouseEnter={() => setHoveredButton('reset')}
            onMouseLeave={() => setHoveredButton(null)}
            onFocus={() => setHoveredButton('reset')}
            onBlur={() => setHoveredButton(null)}
            style={{
              width: '100%',
              height: '3.25rem',
              padding: '0 1.5rem',
              borderRadius: '.375rem',
              fontSize: '1rem',
              lineHeight: '1.5rem',
              fontWeight: 400,
              fontFamily: 'OpenAI Sans, SF Pro, -apple-system, BlinkMacSystemFont, Helvetica, sans-serif',
              backgroundColor: hoveredButton === 'reset' ? '#0d8465' : '#10a37f',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color .1s ease',
            }}
          >
            Send Reset Email
          </button>
        </form>
        <button
          type="button"
          onClick={() => navigate('/login')}
          onMouseEnter={() => setHoveredButton('back')}
          onMouseLeave={() => setHoveredButton(null)}
          onFocus={() => setHoveredButton('back')}
          onBlur={() => setHoveredButton(null)}
          style={{
            width: '100%',
            height: '3.25rem',
            padding: '0 1.5rem',
            borderRadius: '.375rem',
            fontSize: '1rem',
            lineHeight: '1.5rem',
            fontWeight: 400,
            fontFamily: 'OpenAI Sans, SF Pro, -apple-system, BlinkMacSystemFont, Helvetica, sans-serif',
            backgroundColor: hoveredButton === 'back' ? '#f7f7f8' : 'transparent',
            color: '#10a37f',
            border: '1px solid #10a37f',
            cursor: 'pointer',
            marginTop: '1rem',
            transition: 'background-color .1s ease',
          }}
        >
          Back to Login
        </button>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
